import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PublicNavbar from "../../components/PublicNavbar";
import { fetchPublicSettings } from "../../../settings/settingsPublicApi";
import { fetchPublicMenu } from "./menuPublicApi";
import { fetchPublicMonthlySpecials } from "../../../specials/monthlySpecialsPublicApi";
import { useCart } from "../../../cart/useCart";
import "./menu-page.css";

import {
  AlertCircle,
  ArrowRight,
  Cake,
  CakeSlice,
  Check,
  Clock3,
  Croissant,
  Heart,
  Info,
  LoaderCircle,
  MapPin,
  MessageCircle,
  Minus,
  Package,
  Plus,
  ShoppingBag,
  Sparkles,
  Truck,
  X,
} from "lucide-react";

const APPROVED_CATEGORIES = [
  {
    key: "cupcakes",
    label: "Cupcakes",
    matchers: ["cupcake"],
    icon: CakeSlice,
    image: "https://images.unsplash.com/photo-1576618148400-f54bed99fcfd?auto=format&fit=crop&w=1100&q=90",
    description:
      "Moist, fluffy cupcakes finished with rich frosting and premium ingredients. Select your order size to get started, then choose exactly which flavors you want in the next step.",
    subtitle: "Choose your size first, then select your flavors.",
    mixNote: "Half dozen and dozen packs can contain multiple flavors.",
    sizeLabel: "Choose your size",
    sizeKeys: ["individual", "half-dozen", "dozen"],
    large: true,
  },
  {
    key: "pie-pockets",
    label: "Pie Pockets / Dessert Lumpia",
    matchers: ["pie pocket", "dessert lumpia", "lumpia", "pocket"],
    icon: Croissant,
    image: "https://images.unsplash.com/photo-1601050690597-df0568f70950?auto=format&fit=crop&w=1100&q=90",
    description:
      "Golden handheld desserts filled with warm, fruity goodness. Choose a half dozen or dozen first, then mix and match your favorite fillings.",
    subtitle: "Choose your pack size first, then build your flavors.",
    mixNote: "Mix and match flavors in every half-dozen or dozen pack.",
    sizeLabel: "Choose your pack size",
    sizeKeys: ["half-dozen", "dozen"],
    large: true,
  },
  {
    key: "pies",
    label: 'Pies (9")',
    modalTitle: "Pies",
    matchers: ["pie"],
    excludeMatchers: ["pie pocket", "lumpia"],
    icon: Cake,
    image: "https://images.unsplash.com/photo-1562007908-17c67e878c88?auto=format&fit=crop&w=900&q=85",
    description: "Classic pies made with timeless flavors and baked fresh to order.",
    sizeKeys: ["9-inch", "full-size"],
  },
  {
    key: "cheesecakes",
    label: "Cheesecakes",
    matchers: ["cheesecake"],
    icon: CakeSlice,
    image: "https://images.unsplash.com/photo-1524351199678-941a58a3df50?auto=format&fit=crop&w=900&q=85",
    description: "Creamy handcrafted cheesecake made in the flavor your heart desires.",
    sizeKeys: ["individual-4", "mini-6", "full-size"],
  },
];

const SIZE_LABELS = {
  individual: "Individual",
  "half-dozen": "Half Dozen",
  dozen: "Dozen",
  "9-inch": '9" Pie',
  "individual-4": 'Individual 4"',
  "mini-6": 'Mini 6"',
  "full-size": "Full Size",
};

const SIZE_ORDER = ["individual", "individual-4", "mini-6", "half-dozen", "dozen", "9-inch", "full-size"];

function money(value) {
  const amount = Number(value);
  return Number.isFinite(amount) ? `$${amount.toFixed(2)}` : "";
}

function activeRows(rows = []) {
  return [...rows]
    .filter((row) => row && row.is_active !== false && row.is_available !== false)
    .sort((a, b) => {
      const order = Number(a.display_order ?? 0) - Number(b.display_order ?? 0);
      return order || String(a.name || "").localeCompare(String(b.name || ""));
    });
}

function normalizedText(value = "") {
  return String(value)
    .toLowerCase()
    .replace(/&amp;/g, "and")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function matchesSlot(item, slot) {
  const haystack = normalizedText(`${item.category_name || ""} ${item.name || ""}`);
  const includes = slot.matchers.some((matcher) => haystack.includes(normalizedText(matcher)));
  const excludes = slot.excludeMatchers?.some((matcher) => haystack.includes(normalizedText(matcher)));
  return includes && !excludes;
}

function sizeKeyFor(name = "", quantity = 1) {
  const text = normalizedText(name);

  if (text.includes("half") || quantity === 6) return "half-dozen";
  if (text.includes("dozen") || quantity === 12) return "dozen";
  if (text.includes("mini") || text.includes("6 inch") || text.includes("6in") || text === "6") return "mini-6";
  if (text.includes("4 inch") || text.includes("4in") || text.includes("individual 4")) return "individual-4";
  if (text.includes("9") || text.includes("full size 9")) return "9-inch";
  if (text.includes("full")) return "full-size";
  if (text.includes("individual") || text.includes("single") || quantity === 1) return "individual";

  return text.replace(/\s+/g, "-") || "option";
}

function requiredFlavorCount(sizeKey, variant) {
  if (sizeKey === "half-dozen") return 6;
  if (sizeKey === "dozen") return 12;
  return Number(variant?.quantity || 1);
}

function noteFor(sizeKey, variant, slot) {
  const quantity = requiredFlavorCount(sizeKey, variant);
  if (sizeKey === "individual" || sizeKey === "individual-4") return "each";
  if (sizeKey === "mini-6") return "mini";
  if (sizeKey === "half-dozen" || sizeKey === "dozen") {
    const unit = slot.key === "cupcakes" ? "cupcakes" : "pieces";
    return `${quantity} ${unit}`;
  }
  return "";
}

function chooseRepresentativeItem(items, slot) {
  return (
    items.find((item) => activeRows(item.flavors).length || item.requires_flavor) ||
    items.find((item) => activeRows(item.variants).length) ||
    items[0] ||
    null
  );
}

function variantsFor(items, slot) {
  const byKey = new Map();

  items.forEach((item) => {
    activeRows(item.variants).forEach((variant) => {
      const key = sizeKeyFor(variant.name, variant.quantity);
      if (slot.sizeKeys && !slot.sizeKeys.includes(key)) return;

      const current = byKey.get(key);
      const candidate = {
        key,
        id: variant.id,
        label: SIZE_LABELS[key] || variant.name,
        price: variant.price,
        note: noteFor(key, variant, slot),
        quantity: variant.quantity,
        raw: variant,
        item,
      };

      if (!current || Number(candidate.price) < Number(current.price)) {
        byKey.set(key, candidate);
      }
    });
  });

  return [...byKey.values()].sort((a, b) => SIZE_ORDER.indexOf(a.key) - SIZE_ORDER.indexOf(b.key));
}

function flavorsFor(items) {
  const byKey = new Map();

  items.forEach((item) => {
    activeRows(item.flavors).forEach((flavor) => {
      const key = normalizedText(flavor.name);
      if (!byKey.has(key)) {
        byKey.set(key, {
          id: flavor.id,
          source: "flavor",
          label: flavor.name,
          extraPrice: flavor.extra_price,
          raw: flavor,
          item,
        });
      }
    });
  });

  if (byKey.size) return [...byKey.values()];

  items.forEach((item) => {
    const key = normalizedText(item.name);
    if (!byKey.has(key)) {
      byKey.set(key, {
        id: item.id,
        source: "menu_item",
        label: item.name,
        extraPrice: "0.00",
        raw: item,
        item,
      });
    }
  });

  return [...byKey.values()];
}

function priceSummary(variants) {
  const prices = variants.map((variant) => Number(variant.price)).filter(Number.isFinite);
  if (!prices.length) return "Options available";
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  return min === max ? money(min) : `From ${money(min)}`;
}

function buildPresentation(rawItems) {
  return APPROVED_CATEGORIES.map((slot) => {
    const items = activeRows(rawItems).filter((item) => matchesSlot(item, slot));
    const representative = chooseRepresentativeItem(items, slot);
    const variants = variantsFor(items, slot);
    const flavors = flavorsFor(items);

    return {
      ...slot,
      items,
      item: representative,
      id: slot.key,
      title: slot.modalTitle || slot.label,
      sidebarPrice: priceSummary(variants),
      cardPrice: priceSummary(variants),
      variants,
      flavors,
      image: representative?.image_url || slot.image,
      backendImage: representative?.image_url || "",
      description: representative?.description || representative?.short_description || slot.description,
      meta:
        variants.length > 1
          ? variants.map((variant) => `${variant.label} ${money(variant.price)}`).join(" | ")
          : flavors.slice(0, 4).map((flavor) => flavor.label).join(", "),
    };
  });
}

function SizeOption({ option, selected, onSelect }) {
  return (
    <button type="button" className={`menu-size-option ${selected ? "menu-size-option--active" : ""}`} onClick={() => onSelect(option.key)}>
      <span className="menu-size-radio"><span /></span>
      <span className="menu-size-copy">
        <strong>{option.label}</strong>
        <span>{money(option.price)}{option.note && <small> {option.note}</small>}</span>
      </span>
    </button>
  );
}

function CustomizableProduct({ product, selectedSize, onSelectSize, onOpen }) {
  const Icon = product.icon;
  const preview = product.flavors.map((flavor) => flavor.label);

  return (
    <section className="menu-builder-card" id={product.key}>
      <div className="menu-builder-heading">
        <div>
          <div className="menu-builder-title-row">
            <span className="menu-category-symbol"><Icon size={25} strokeWidth={1.6} /></span>
            <div>
              <h2>{product.label}</h2>
              <p>{product.subtitle}</p>
            </div>
          </div>
        </div>
        <span className="menu-mix-badge"><Package size={13} strokeWidth={1.7} />Mix &amp; match flavors</span>
      </div>

      <div className="menu-builder-body">
        <div className="menu-builder-image-wrap">
          <img src={product.image} alt={product.label} className="menu-builder-image" />
        </div>
        <div className="menu-builder-content">
          <p className="menu-builder-description">{product.description}</p>
          <div className="menu-flavor-preview">
            <span className="menu-flavor-dot" />
            <strong>Popular flavors:</strong>
            <span>{preview.length ? `${preview.slice(0, 5).join(", ")}${preview.length > 5 ? ", and more." : "."}` : "Options available in the next step."}</span>
          </div>
          <div className="menu-size-label">{product.sizeLabel}</div>
          <div className={`menu-size-grid ${product.variants.length === 2 ? "menu-size-grid--two" : ""}`}>
            {product.variants.map((size) => (
              <SizeOption key={size.key} option={size} selected={selectedSize === size.key} onSelect={onSelectSize} />
            ))}
          </div>
          <div className="menu-builder-bottom">
            <div className="menu-mix-note"><Package size={15} strokeWidth={1.7} /><span>{product.mixNote}</span></div>
            <button type="button" className="menu-primary-button" disabled={!product.variants.length} onClick={() => onOpen(product)}>
              Choose flavors <ArrowRight size={15} strokeWidth={1.8} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function specialPriceText(special) {
  const price = money(special?.final_price);
  if (!price) return "Special pricing";
  return price;
}

function specialTypeText(type = "") {
  return String(type || "")
    .replace(/_/g, " ")
    .split(" ")
    .map((word) => (word ? `${word[0].toUpperCase()}${word.slice(1)}` : ""))
    .join(" ");
}

function specialDateText(special) {
  const options = { month: "short", day: "numeric" };
  const start = special?.starts_at ? new Date(special.starts_at).toLocaleDateString(undefined, options) : "";
  const end = special?.ends_at ? new Date(special.ends_at).toLocaleDateString(undefined, options) : "";
  if (start && end) return `${start} - ${end}`;
  if (end) return `Through ${end}`;
  if (start) return `Starts ${start}`;
  return "";
}

function specialMetaText(special) {
  const items = Array.isArray(special?.items) ? special.items : [];
  const linkedItems = items
    .slice(0, 3)
    .map((item) => item.display_title || item.display_name || item.menu_item_name)
    .filter(Boolean)
    .join(" | ");
  return [specialTypeText(special?.special_type), linkedItems, specialDateText(special)]
    .filter(Boolean)
    .join(" | ") || special?.badge_text || "Limited-time special";
}

function MonthlySpecialMenuCard({ special, onAdd }) {
  const hasImage = Boolean(special.image_url);
  const isOrderable = Array.isArray(special.items) && special.items.length > 0 && Number(special.final_price) >= 0;

  return (
    <article className="menu-standard-card menu-monthly-special-card">
      <div className="menu-standard-image-wrap">
        {hasImage ? (
          <img src={special.image_url} alt={special.name} className="menu-standard-image" />
        ) : (
          <div className="menu-image-placeholder"><Sparkles size={26} strokeWidth={1.5} /></div>
        )}
        <span className="menu-monthly-special-badge">{special.badge_text || "Monthly Special"}</span>
      </div>
      <div className="menu-standard-body">
        <h3>{special.name}</h3>
        <p>{special.short_description || special.description || "Limited-time sweets from the SugaLumps kitchen."}</p>
        <span className="menu-standard-meta">{specialMetaText(special)}</span>
        <div className="menu-standard-footer">
          <strong>{specialPriceText(special)}</strong>
          <button type="button" disabled={!isOrderable} onClick={() => onAdd(special)}>
            {isOrderable ? "Add special" : "Details soon"}
          </button>
        </div>
      </div>
    </article>
  );
}

function StandardMenuCard({ item, onOpen }) {
  return (
    <article className="menu-standard-card" id={item.key}>
      <div className="menu-standard-image-wrap">
        <img src={item.image} alt={item.label} className="menu-standard-image" />
      </div>
      <div className="menu-standard-body">
        <h3>{item.label}</h3>
        <p>{item.description}</p>
        {item.meta && <span className="menu-standard-meta">{item.meta}</span>}
        <div className="menu-standard-footer">
          <strong>{item.cardPrice}</strong>
          <button type="button" onClick={() => onOpen(item)}>View options</button>
        </div>
      </div>
    </article>
  );
}

function FlavorRow({
  flavor,
  mode,
  selected = false,
  quantity = 0,
  onSelect,
  onDecrement,
  onIncrement,
  incrementDisabled = false,
}) {
  if (mode === "single") {
    return (
      <button
        type="button"
        className={`menu-modal-flavor-row menu-modal-flavor-row--single ${selected ? "is-selected" : ""}`}
        onClick={onSelect}
      >
        <span className="menu-modal-radio"><span /></span>
        <span>{flavor.label}</span>
      </button>
    );
  }

  return (
    <div className="menu-modal-flavor-row">
      <span>{flavor.label}</span>
      <div>
        <button type="button" className="flavor-qty-button" disabled={quantity <= 0} onClick={onDecrement}><Minus size={14} /></button>
        <strong>{quantity}</strong>
        <button type="button" className="flavor-qty-button" disabled={incrementDisabled} onClick={onIncrement}><Plus size={14} /></button>
      </div>
    </div>
  );
}

function ConfigureModal({ product, selectedSizeKey, onSelectSize, onClose, onAdd }) {
  const [quantities, setQuantities] = useState({});
  const [selectedFlavorId, setSelectedFlavorId] = useState(null);
  const selectedVariant = product.variants.find((variant) => variant.key === selectedSizeKey) || product.variants[0];
  const total = Object.values(quantities).reduce((sum, value) => sum + Number(value || 0), 0);
  const required = product.large ? requiredFlavorCount(selectedVariant?.key, selectedVariant) : 1;
  const needsFlavorCounts = product.large && required > 1 && product.flavors.length > 0;
  const needsSingleFlavor = !needsFlavorCounts && product.flavors.length > 0;
  const packComplete = needsFlavorCounts && total >= required;
  const canAdd = Boolean(
    selectedVariant &&
      (needsFlavorCounts
        ? total === required
        : needsSingleFlavor
        ? selectedFlavorId
        : true),
  );

  useEffect(() => {
    setQuantities({});
    setSelectedFlavorId(null);
  }, [product.key, selectedSizeKey]);

  function decrementFlavor(flavorId) {
    setQuantities((current) => ({
      ...current,
      [flavorId]: Math.max(0, (current[flavorId] || 0) - 1),
    }));
  }

  function incrementFlavor(flavorId) {
    setQuantities((current) => {
      const currentTotal = Object.values(current).reduce(
        (sum, quantity) => sum + Number(quantity || 0),
        0,
      );

      if (currentTotal >= required) {
        return current;
      }

      return {
        ...current,
        [flavorId]: (current[flavorId] || 0) + 1,
      };
    });
  }

  function addToCart() {
    if (!canAdd) {
      return;
    }

    const selectedFlavors = needsFlavorCounts
      ? product.flavors
          .map((flavor) => ({ ...flavor, quantity: quantities[flavor.id] || 0 }))
          .filter((flavor) => flavor.quantity > 0)
      : product.flavors
          .filter((flavor) => flavor.id === selectedFlavorId)
          .map((flavor) => ({ ...flavor, quantity: 1 }));

    onAdd({ product, variant: selectedVariant, selectedFlavors });
  }

  return (
    <div className="menu-modal-backdrop">
      <section className="menu-config-modal" role="dialog" aria-modal="true" aria-label={`${product.title} options`}>
        <header>
          <div>
            <span className="menu-eyebrow">Build your order</span>
            <h2>{product.title}</h2>
          </div>
          <button type="button" className="menu-modal-close" onClick={onClose}><X size={20} /></button>
        </header>

        <div className={`menu-size-grid ${product.variants.length === 2 ? "menu-size-grid--two" : ""}`}>
          {product.variants.map((size) => (
            <SizeOption key={size.key} option={size} selected={selectedVariant?.key === size.key} onSelect={onSelectSize} />
          ))}
        </div>

        {needsFlavorCounts ? (
          <div className="menu-modal-flavors">
            {product.flavors.map((flavor) => (
              <FlavorRow
                key={flavor.id}
                flavor={flavor}
                mode="quantity"
                quantity={quantities[flavor.id] || 0}
                onDecrement={() => decrementFlavor(flavor.id)}
                onIncrement={() => incrementFlavor(flavor.id)}
                incrementDisabled={packComplete}
              />
            ))}
            <p className={total === required ? "is-complete" : ""}>{total} of {required} selected</p>
          </div>
        ) : product.flavors.length ? (
          <div className="menu-modal-flavor-list">
            {product.flavors.map((flavor) => (
              <FlavorRow
                key={flavor.id}
                flavor={flavor}
                mode="single"
                selected={selectedFlavorId === flavor.id}
                onSelect={() => setSelectedFlavorId(flavor.id)}
              />
            ))}
          </div>
        ) : (
          <p className="menu-modal-helper">No flavor choices are configured for this item yet.</p>
        )}

        <footer>
          <button type="button" className="menu-modal-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="menu-primary-button" disabled={!canAdd} onClick={addToCart}>Add to Cart</button>
        </footer>
      </section>
    </div>
  );
}

export default function MenuPage() {
  const [menuItems, setMenuItems] = useState([]);
  const [monthlySpecials, setMonthlySpecials] = useState([]);
  const [settings, setSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeCategory, setActiveCategory] = useState("cupcakes");
  const [selectedSizes, setSelectedSizes] = useState({ cupcakes: "individual", "pie-pockets": "half-dozen" });
  const [modalProduct, setModalProduct] = useState(null);
  const { items: cartItems, itemCount, subtotal, addItem, removeItem } = useCart();
  const [toast, setToast] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const controller = new AbortController();
    async function loadMenu() {
      setIsLoading(true);
      setError("");
      try {
        const [items, publicSettings, specials] = await Promise.all([
          fetchPublicMenu({ signal: controller.signal }),
          fetchPublicSettings({ signal: controller.signal }),
          fetchPublicMonthlySpecials({ signal: controller.signal }),
        ]);
        setMenuItems(items);
        setSettings(publicSettings);
        setMonthlySpecials(specials);
      } catch (err) {
        if (err.name !== "AbortError") setError(err.message || "The menu could not be loaded.");
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    }
    loadMenu();
    return () => controller.abort();
  }, []);

  const products = useMemo(() => buildPresentation(menuItems), [menuItems]);
  const largeProducts = products.filter((product) => product.large);
  const standardProducts = products.filter((product) => !product.large);
  const orderableMonthlySpecials = useMemo(
    () => monthlySpecials.filter((special) => Array.isArray(special.items) && special.items.length > 0),
    [monthlySpecials],
  );

  useEffect(() => {
    setSelectedSizes((current) => {
      const next = { ...current };
      products.forEach((product) => {
        if (!next[product.key] && product.variants[0]) next[product.key] = product.variants[0].key;
      });
      return next;
    });
  }, [products]);

  function selectSize(productKey, sizeKey) {
    setSelectedSizes((current) => ({ ...current, [productKey]: sizeKey }));
  }

  function openProduct(product) {
    setActiveCategory(product.key);
    setModalProduct(product);
  }

  function handleSidebarClick(product) {
    setActiveCategory(product.key);
    if (product.large) {
      document.getElementById(product.key)?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      openProduct(product);
    }
  }

  function addMonthlySpecial(special) {
    const line = {
      id: `monthly-special-${special.id}-${Date.now()}`,
      product_key: `monthly-special-${special.id}`,
      special_id: special.id,
      monthly_special_id: special.id,
      quantity: 1,
      unit_price: special.final_price || "0.00",
      label: special.name,
      item_name_snapshot: special.name,
      variant_name_snapshot: special.badge_text || "Monthly Special",
      image: special.image_url || "",
      category_name: "Monthly Special",
      special_items: Array.isArray(special.items) ? special.items.map((item) => item.id) : [],
      flavors: [],
    };
    addItem(line);
    setToast({ title: "Added to your order", body: `${line.label} added to cart.` });
    window.setTimeout(() => setToast(null), 3200);
  }

  function addConfiguredItem({ product, variant, selectedFlavors }) {
    const baseItem = variant?.item || product.item;
    const line = {
      id: `${product.key}-${variant?.id || "variant"}-${Date.now()}`,
      product_key: product.key,
      menu_item_id: baseItem?.id || null,
      variant_id: variant?.id || null,
      flavor_ids: selectedFlavors.filter((flavor) => flavor.source === "flavor").map((flavor) => flavor.id),
      flavor_menu_item_ids: selectedFlavors.filter((flavor) => flavor.source === "menu_item").map((flavor) => flavor.id),
      flavors: selectedFlavors.map((flavor) => ({ id: flavor.id, label: flavor.label, quantity: flavor.quantity })),
      quantity: 1,
      unit_price: variant?.price || "0.00",
      label: `${variant?.label || ""} ${product.title}`.trim(),
      item_name_snapshot: product.title,
      variant_name_snapshot: variant?.label || "",
      image: product.backendImage || "",
      category_name: product.label,
    };
    addItem(line);
    setModalProduct(null);
    setToast({ title: "Added to your order", body: `${line.label} added to cart.` });
    window.setTimeout(() => setToast(null), 3200);
  }

  return (
    <div className="sugalumps-menu-page">
      <PublicNavbar />
      <main className="menu-page-main">
        <section className="menu-page-intro">
          <div>
            <span className="menu-eyebrow">SUGALUMPS MENU</span>
            <h1>{settings?.menu_heading || "Order Sweet Treats"}</h1>
            <p>{settings?.menu_subheading || "Handcrafted desserts made to be remembered."}</p>
          </div>
          <div className="menu-service-details">
            <div className="menu-service-item"><span className="menu-service-icon"><Truck size={20} strokeWidth={1.6} /></span><div><strong>Delivery only</strong><small>No pickup available</small></div></div>
            <div className="menu-service-item"><span className="menu-service-icon"><MapPin size={20} strokeWidth={1.6} /></span><div><strong>Silver Spring, MD</strong><small>Serving the DMV area</small></div></div>
            <div className="menu-service-item"><span className="menu-service-icon"><Sparkles size={20} strokeWidth={1.6} /></span><div><strong>Made to order</strong><small>Fresh, handcrafted sweets</small></div></div>
          </div>
        </section>

        <div className="menu-helper-banner"><span className="menu-helper-icon"><Info size={14} strokeWidth={2} /></span><p><strong>Customizable items work a little differently.</strong> Choose the size or pack first, then build your flavor selection in the next step.</p></div>

        {isLoading && <div className="menu-state-message"><LoaderCircle size={18} className="menu-spin" />Loading menu from SugaLumps...</div>}
        {error && <div className="menu-state-message menu-state-message--error"><AlertCircle size={18} />{error}</div>}

        {!isLoading && !error && (
          <div className="menu-layout">
            <aside className="menu-category-sidebar">
              <div className="menu-category-list">
                {products.map((category) => {
                  const Icon = category.icon;
                  return (
                    <button type="button" key={category.key} className={`menu-category-button ${activeCategory === category.key ? "menu-category-button--active" : ""}`} onClick={() => handleSidebarClick(category)}>
                      <span className="menu-category-icon"><Icon size={20} strokeWidth={1.7} /></span>
                      <span className="menu-category-copy"><strong>{category.label}</strong><small>{category.sidebarPrice}</small></span>
                    </button>
                  );
                })}
              </div>
              <div className="menu-sidebar-card menu-care-card"><span className="menu-sidebar-heart"><Heart size={28} strokeWidth={1.5} /></span><h3>Made with care</h3><p>Every dessert is handcrafted to order using premium ingredients. Final selections happen after you choose your item.</p></div>
              <div className="menu-sidebar-card">
                <p className="menu-special-question">Questions or special requests?</p>
                <button
                  type="button"
                  className="menu-contact-button"
                  onClick={() => navigate("/contact")}
                >
                  <MessageCircle size={15} strokeWidth={1.7} />
                  Contact Us
                </button>
              </div>
            </aside>

            <div className="menu-products-column">
              {orderableMonthlySpecials.length > 0 && (
                <section className="menu-more-section menu-monthly-specials-section">
                  <div className="menu-more-heading"><div><span className="menu-eyebrow">MONTHLY SPECIALS</span><h2>Limited-time treats.</h2></div><p>Featured specials are pulled from the current SugaLumps menu and priced by the bakery.</p></div>
                  <div className="menu-standard-grid">{orderableMonthlySpecials.map((special) => <MonthlySpecialMenuCard key={special.id} special={special} onAdd={addMonthlySpecial} />)}</div>
                </section>
              )}

              {largeProducts.map((product) => (
                <CustomizableProduct key={product.key} product={product} selectedSize={selectedSizes[product.key]} onSelectSize={(sizeKey) => selectSize(product.key, sizeKey)} onOpen={openProduct} />
              ))}

              <section className="menu-more-section">
                <div className="menu-more-heading"><div><span className="menu-eyebrow">MORE SWEET TREATS</span><h2>Something for every craving.</h2></div><p>Browse the rest of the menu and choose options when you find something you love.</p></div>
                <div className="menu-standard-grid">{standardProducts.map((item) => <StandardMenuCard key={item.key} item={item} onOpen={openProduct} />)}</div>
              </section>

              <section className="menu-dietary-note">
                <div><Sparkles size={21} strokeWidth={1.5} /><p><strong>Dietary needs?</strong>{settings?.allergy_disclaimer || "All products can be made with dietary restrictions at an additional cost."}</p></div>
                <div><Sparkles size={21} strokeWidth={1.5} /><p><strong>Want a grown-up version?</strong>Products can also be spiked with liquor at an additional cost.</p></div>
              </section>
            </div>

            <aside className="menu-order-sidebar">
              <div className="menu-order-heading"><ShoppingBag size={19} strokeWidth={1.7} /><h2>Your Order</h2></div>
              {cartItems.length ? (
                <div className="menu-cart-list">
                  {cartItems.map((item) => (
                    <article className="menu-cart-line" key={item.id}>
                      <div className="menu-cart-line-head">
                        <strong>{item.label}</strong>
                        <button type="button" aria-label={`Remove ${item.label} from order`} onClick={() => removeItem(item.id)}>
                          <X size={13} strokeWidth={2} />
                        </button>
                      </div>
                      <small>{money(item.unit_price)}</small>
                      {item.flavors?.length > 0 && <span>{item.flavors.map((flavor) => `${flavor.label} x${flavor.quantity}`).join(", ")}</span>}
                    </article>
                  ))}
                  <Link to="/cart" className="menu-view-cart-button">
                    View Cart &amp; Checkout <ArrowRight size={14} strokeWidth={1.8} />
                  </Link>
                </div>
              ) : (
                <div className="menu-empty-cart"><div className="menu-empty-icon"><Heart className="menu-empty-heart-icon" size={18} fill="currentColor" strokeWidth={0} /><ShoppingBag className="menu-empty-bag-icon" size={63} strokeWidth={1.25} /></div><h3>Your cart is empty</h3><p>Looks like you haven't added<br />anything yet.</p><button type="button" className="menu-start-order-button" onClick={() => document.getElementById("cupcakes")?.scrollIntoView({ behavior: "smooth" })}>Start your order</button></div>
              )}
              <div className="menu-order-info">
                <div className="menu-order-info-row"><Truck size={17} strokeWidth={1.6} /><div><strong>Delivery only</strong><small>No pickup available</small></div></div>
                <div className="menu-order-info-row"><MapPin size={17} strokeWidth={1.6} /><div><strong>Delivering to</strong><small>Silver Spring, MD</small><button type="button">Change</button></div></div>
                <div className="menu-order-info-row"><Clock3 size={17} strokeWidth={1.6} /><div><strong>Estimated time</strong><small>60-90 min</small></div></div>
                <div className="menu-order-info-row"><MapPin size={17} strokeWidth={1.6} /><div><strong>Service area</strong><small>Silver Spring &amp; DMV</small><button type="button">View areas</button></div></div>
              </div>
              <p className="menu-cart-disclaimer">Prices may vary by selection. Final details will be confirmed at checkout.</p>
            </aside>
          </div>
        )}
      </main>

      {cartItems.length > 0 && (
        <Link to="/cart" className="menu-mobile-cart-cta" aria-label={`View order with ${itemCount} ${itemCount === 1 ? "item" : "items"}`}>
          <span>
            <ShoppingBag size={18} strokeWidth={1.8} />
            View Order ({itemCount})
          </span>
          <strong>{money(subtotal)}</strong>
          <ArrowRight size={16} strokeWidth={1.8} />
        </Link>
      )}

      {modalProduct && <ConfigureModal product={modalProduct} selectedSizeKey={selectedSizes[modalProduct.key]} onSelectSize={(sizeKey) => selectSize(modalProduct.key, sizeKey)} onClose={() => setModalProduct(null)} onAdd={addConfiguredItem} />}
      {toast && <div className="menu-toast"><Check size={18} /><div><strong>{toast.title}</strong><span>{toast.body}</span></div></div>}
    </div>
  );
}
